import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from '../utils/axiosConfig';

const SliderCarousel = () => {
    const { t, i18n } = useTranslation();
    const [sliders, setSliders] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        fetchSliders();
    }, []);

    useEffect(() => {
        if (sliders.length === 0 || isPaused) return;

        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % sliders.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [sliders.length, isPaused]);

    const fetchSliders = async () => {
        try {
            const response = await axios.get('/api/sliders');
            setSliders(response.data);
        } catch (err) {
            console.error('Error fetching sliders:', err);
        }
    };

    const handlePrev = () => {
        setCurrentSlide((prev) => (prev - 1 + sliders.length) % sliders.length);
    };

    const handleNext = () => {
        setCurrentSlide((prev) => (prev + 1) % sliders.length);
    };

    if (sliders.length === 0) return null;

    return (
        <Box
            sx={{
                height: '400px',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2,
                mb: 4,
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {sliders.map((slide, index) => (
                <Box
                    key={slide.id}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: index === currentSlide ? 1 : 0,
                        transition: 'opacity 1s ease-in-out',
                        backgroundImage: slide.backgroundImage
                            ? `url(${axios.defaults.baseURL}/${slide.backgroundImage})`
                            : 'linear-gradient(135deg, #432312 0%, #e2571f 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: slide.layout === 'left' ? 'flex-start' :
                            slide.layout === 'right' ? 'flex-end' : 'center',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        },
                    }}
                >
                    <Box sx={{
                        position: 'relative',
                        zIndex: 1,
                        textAlign: slide.layout || 'center',
                        color: 'white',
                        px: 3,
                        maxWidth: '600px',
                        marginLeft: slide.layout === 'left' ? 5 : 'auto',
                        marginRight: slide.layout === 'right' ? 5 : 'auto',
                    }}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {i18n.language === 'fr' ? slide.frenchTitle : slide.englishTitle}
                        </Typography>
                        {(slide.englishSubtitle || slide.frenchSubtitle) && (
                            <Typography variant="h6">
                                {i18n.language === 'fr' ? slide.frenchSubtitle : slide.englishSubtitle}
                            </Typography>
                        )}
                        {i18n.language === 'en' ? (
                            slide.englishCtaText && slide.englishCtaLink && (
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{ mt: 3 }}
                                    href={slide.englishCtaLink.startsWith('http') ? slide.englishCtaLink : `https://${slide.englishCtaLink}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {slide.englishCtaText}
                                </Button>
                            )) : (
                            slide.frenchCtaText && slide.frenchCtaLink && (
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{ mt: 3 }}
                                    href={slide.frenchCtaLink.startsWith('http') ? slide.frenchCtaLink : `https://${slide.frenchCtaLink}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {slide.frenchCtaText}
                                </Button>
                            ))}
                    </Box>
                </Box>
            ))}

            {/* Navigation Buttons */}
            <IconButton
                onClick={handlePrev}
                sx={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' },
                    color: 'white',
                }}
                size="small"
            >
                <ArrowBackIos fontSize="small" />
            </IconButton>
            <IconButton
                onClick={handleNext}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' },
                    color: 'white',
                }}
                size="small"
            >
                <ArrowForwardIos fontSize="small" />
            </IconButton>

            {/* Slide Counter */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: 1,
                }}
            >
                <Typography variant="caption">
                    {currentSlide + 1} {t('sliderCarousel.of')} {sliders.length}
                </Typography>
            </Box>
        </Box>
    );
};

export default SliderCarousel;
